/**
 * Impuls order helpers.
 * - [data-impuls-order] buttons open the order form: they click the COD app button
 *   (selector from the sticky bar setting), or fall back to submitting the main add-to-cart form.
 * - <impuls-sticky> shows its bar once the main buy area has scrolled out of view.
 */

/** @returns {HTMLFormElement | null} */
function findMainCartForm() {
  return document.querySelector('.shopify-section[id$="__main"] form[action$="/cart/add"], form[action$="/cart/add"]');
}

/** @returns {string} */
function orderSelector() {
  const sticky = document.querySelector('impuls-sticky');
  return sticky?.getAttribute('data-order-selector')?.trim() ?? '';
}

/** @param {Element} el */
function scrollToElement(el) {
  const smooth = !window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  el.scrollIntoView({ behavior: smooth ? 'smooth' : 'auto', block: 'center' });
}

function openOrderForm() {
  const selector = orderSelector();
  if (selector) {
    let appButton = null;
    try {
      appButton = document.querySelector(selector);
    } catch {
      appButton = null;
    }
    if (appButton instanceof HTMLElement) {
      appButton.click();
      return;
    }
  }

  const form = findMainCartForm();
  const submit = form?.querySelector('[type="submit"]:not([disabled])');
  if (submit instanceof HTMLElement) {
    submit.click();
    return;
  }

  if (form) scrollToElement(form);
}

if (!window.__impulsOrderBound) {
  window.__impulsOrderBound = true;
  document.addEventListener('click', (event) => {
    const target = event.target instanceof Element ? event.target.closest('[data-impuls-order]') : null;
    if (!target || target.hasAttribute('disabled')) return;
    event.preventDefault();
    openOrderForm();
  });
}

/**
 * The COD app (EasySell) injects its button with inline !important colours.
 * Re-apply the store's green style and Romanian label whenever the app (re)renders it.
 */
const ES_GREEN = '#1f8f4e';
const MAIN_PRODUCT = '.shopify-section[id$="__main"]';

/**
 * Hide an app element and keep it hidden if the app re-shows it via inline styles.
 * @param {HTMLElement} el
 */
function forceHidden(el) {
  if (el.style.getPropertyValue('display') !== 'none') el.style.setProperty('display', 'none', 'important');
  if (el.dataset.impulsHidden) return;
  el.dataset.impulsHidden = 'true';
  new MutationObserver(() => {
    if (el.style.getPropertyValue('display') !== 'none') el.style.setProperty('display', 'none', 'important');
  }).observe(el, { attributes: true, attributeFilter: ['style', 'class'] });
}

function styleCodButtons() {
  // The app's own floating bar duplicates the theme sticky bar (and is English); hide it.
  for (const bar of document.querySelectorAll('#es-sticky, .es-sticky-btn')) {
    if (bar instanceof HTMLElement) forceHidden(bar);
  }

  const productLabel = document.querySelector('[data-es-label]')?.getAttribute('data-es-label')?.trim() ?? '';
  const cartLabel = document.querySelector('[data-es-label-cart]')?.getAttribute('data-es-label-cart')?.trim() ?? '';

  const isProductPage = document.getElementById('MainContent')?.dataset.template?.startsWith('product') ?? false;

  for (const btn of document.querySelectorAll('.es-popup-button')) {
    if (!(btn instanceof HTMLElement)) continue;
    const inCart = btn.closest('.cart__ctas, cart-drawer, .cart-drawer, .main-cart') !== null;
    const inMainProduct = isProductPage && btn.closest(MAIN_PRODUCT) !== null;
    if (!inCart && !inMainProduct) {
      // Product cards link to the full product page; the quick COD button there only clutters the grid.
      forceHidden(btn);
      continue;
    }
    const label = inCart ? cartLabel : productLabel;
    btn.classList.add('impuls-es-btn');
    btn.classList.toggle('impuls-es-btn--main', inMainProduct);
    if (btn.style.getPropertyValue('background') !== ES_GREEN) {
      btn.style.setProperty('background', ES_GREEN, 'important');
      btn.style.setProperty('border-color', ES_GREEN, 'important');
      btn.style.setProperty('border-radius', '10px', 'important');
    }
    const title = btn.querySelector('.es-button-title');
    if (label && title && title.textContent !== label) title.textContent = label;
  }
}

/**
 * Shopify's cookie banner follows the store language (English for now). Translate the main banner to
 * Romanian with the same meaning and point the policy link to the Romanian privacy page. Does nothing
 * once Shopify itself shows the banner in Romanian.
 */
function translateCookieBanner() {
  const banner = document.getElementById('shopify-pc__banner');
  if (!banner || banner.dataset.impulsRo) return;
  const title = banner.querySelector('#shopify-pc__banner__body-title');
  if (!title || !/cookie consent/i.test(title.textContent ?? '')) return;
  banner.dataset.impulsRo = 'true';
  title.textContent = 'Cookie-uri';
  const text = banner.querySelector('.shopify-pc__banner__body p');
  const link = text?.querySelector('a');
  if (text) {
    text.textContent =
      'Noi și partenerii noștri, inclusiv Shopify, folosim cookie-uri și alte tehnologii ca să îți personalizăm experiența, să îți arătăm reclame și pentru statistici. Nu le folosim în aceste scopuri decât dacă ești de acord. Detalii în ';
    if (link) {
      link.textContent = 'Politica de confidențialitate';
      link.setAttribute('href', `${window.Shopify?.routes?.root ?? '/'}pages/politica-de-confidentialitate`);
      text.append(link, '.');
    }
  }
  const labels = {
    'shopify-pc__banner__btn-accept': 'Accept',
    'shopify-pc__banner__btn-decline': 'Refuz',
    'shopify-pc__banner__btn-manage-prefs': 'Setări cookie-uri',
  };
  for (const [id, label] of Object.entries(labels)) {
    const btn = banner.querySelector(`#${id}`);
    if (btn) btn.textContent = label;
  }
}

if (!window.__impulsCodObserver) {
  styleCodButtons();
  translateCookieBanner();
  let queued = false;
  window.__impulsCodObserver = new MutationObserver(() => {
    if (queued) return;
    queued = true;
    requestAnimationFrame(() => {
      queued = false;
      styleCodButtons();
      translateCookieBanner();
    });
  });
  window.__impulsCodObserver.observe(document.body, { childList: true, subtree: true });
}

class ImpulsSticky extends HTMLElement {
  /** @type {IntersectionObserver | undefined} */
  #observer;

  connectedCallback() {
    const bar = this.querySelector('.impuls-sticky__bar');
    const anchor = findMainCartForm();
    if (!(bar instanceof HTMLElement)) return;

    if (!anchor) {
      bar.dataset.visible = 'true';
      return;
    }

    this.#observer = new IntersectionObserver(([entry]) => {
      if (!entry) return;
      const above = entry.boundingClientRect.top < 0;
      bar.dataset.visible = String(!entry.isIntersecting && above);
    });
    this.#observer.observe(anchor);
  }

  disconnectedCallback() {
    this.#observer?.disconnect();
  }
}

if (!customElements.get('impuls-sticky')) {
  customElements.define('impuls-sticky', ImpulsSticky);
}

/* ---------- Quantity offers + sticky stepper ---------- */

const MAX_QTY = 10;
const moneyFormat = new Intl.NumberFormat('ro-RO', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

/** @param {number} cents */
function formatMoney(cents) {
  return `${moneyFormat.format(cents / 100)} lei`;
}

/** @returns {HTMLInputElement | null} */
function mainQuantityInput() {
  const input =
    document.querySelector(`${MAIN_PRODUCT} form[action$="/cart/add"] input[name="quantity"]`) ??
    document.querySelector('form[action$="/cart/add"] input[name="quantity"]');
  return input instanceof HTMLInputElement ? input : null;
}

function offerPricing() {
  const offers = document.querySelector('impuls-offers');
  const sticky = document.querySelector('impuls-sticky');
  return {
    price: Number(offers?.getAttribute('data-price') ?? sticky?.getAttribute('data-price') ?? 0),
    d2: Number(offers?.getAttribute('data-d2') ?? 0),
    d3: Number(offers?.getAttribute('data-d3') ?? 0),
  };
}

/** Total in cents for a quantity, matching the Shopify automatic discount tiers. @param {number} qty */
function totalFor(qty) {
  const { price, d2, d3 } = offerPricing();
  const discount = qty >= 3 ? d3 : qty === 2 ? d2 : 0;
  return Math.round((price * qty * (100 - discount)) / 100);
}

/** @param {number} value */
function setQuantity(value) {
  const qty = Math.max(1, Math.min(MAX_QTY, Math.round(value) || 1));
  window.__impulsQty = qty;

  const input = mainQuantityInput();
  if (input && input.value !== String(qty)) {
    input.value = String(qty);
    input.dispatchEvent(new Event('input', { bubbles: true }));
    input.dispatchEvent(new Event('change', { bubbles: true }));
  }

  const tier = Math.min(qty, 3);
  for (const card of document.querySelectorAll('.impuls-offer-card')) {
    if (!(card instanceof HTMLElement)) continue;
    const selected = Number(card.dataset.qty) === tier;
    card.classList.toggle('is-selected', selected);
    const radio = card.querySelector('input');
    if (radio instanceof HTMLInputElement) radio.checked = selected;
  }

  for (const el of document.querySelectorAll('[data-impuls-qty-value]')) el.textContent = String(qty);
  for (const el of document.querySelectorAll('[data-impuls-total]')) el.textContent = formatMoney(totalFor(qty));
}

function addMainProductToCart() {
  const submit = findMainCartForm()?.querySelector('[type="submit"]:not([disabled])');
  if (submit instanceof HTMLElement) submit.click();
}

if (!window.__impulsQtyBound) {
  window.__impulsQtyBound = true;

  document.addEventListener('change', (event) => {
    const target = event.target;
    if (target instanceof HTMLInputElement && target.classList.contains('impuls-offer-card__input')) {
      setQuantity(Number(target.value));
    }
  });

  document.addEventListener('click', (event) => {
    if (!(event.target instanceof Element)) return;
    const step = event.target.closest('[data-impuls-step]');
    if (step instanceof HTMLElement) {
      setQuantity((window.__impulsQty ?? 1) + Number(step.dataset.impulsStep));
      return;
    }
    if (event.target.closest('[data-impuls-add]')) {
      event.preventDefault();
      addMainProductToCart();
    }
  });

  const initial = document.querySelector('.impuls-offer-card__input:checked');
  if (initial instanceof HTMLInputElement) {
    setQuantity(Number(initial.value));
  } else if (document.querySelector('impuls-sticky')) {
    setQuantity(Number(mainQuantityInput()?.value ?? 1));
  }
}


/**
 * Header cart (sections/impuls-header.liquid): keeps the item count badge in sync after any cart change
 * (add, quantity, remove) and opens the theme cart drawer instead of leaving the page, when it exists.
 */
async function refreshCartBadge() {
  const badge = document.querySelector('.impuls-head__count');
  if (!(badge instanceof HTMLElement)) return;
  try {
    const response = await fetch(`${window.Shopify?.routes?.root ?? '/'}cart.js`, { headers: { Accept: 'application/json' } });
    const cart = await response.json();
    badge.textContent = String(cart.item_count);
    badge.hidden = cart.item_count === 0;
  } catch {
    // Badge stays as rendered; the cart itself is unaffected.
  }
}

if (!window.__impulsCartBound) {
  window.__impulsCartBound = true;

  // Watch cart requests without touching the theme's own fetch code.
  if ('PerformanceObserver' in window) {
    let timer = 0;
    new PerformanceObserver((list) => {
      if (list.getEntries().some((entry) => /\/cart\/(add|change|update|clear)/.test(entry.name))) {
        clearTimeout(timer);
        timer = window.setTimeout(refreshCartBadge, 300);
      }
    }).observe({ type: 'resource', buffered: false });
  }

  document.addEventListener('click', (event) => {
    if (!(event.target instanceof Element)) return;
    const link = event.target.closest('[data-impuls-cart-link]');
    const drawer = document.getElementById('cart-drawer');
    if (!link || !drawer || typeof drawer.open !== 'function') return;
    event.preventDefault();
    drawer.open();
  });
}

/**
 * Card-only discount (Theme settings → "Casello – date magazin"): the code is applied by the
 * "Plătește cu cardul" link. Before the cash-on-delivery form (EasySell) opens, remove that code from
 * the cart so cash orders never get the card discount.
 */
function cardCode() {
  return document.querySelector('[data-impuls-card-code]')?.getAttribute('data-impuls-card-code')?.trim() ?? '';
}

async function removeCardCode() {
  const code = cardCode().toUpperCase();
  if (!code) return;
  const root = window.Shopify?.routes?.root ?? '/';
  const cart = await fetch(`${root}cart.js`, { headers: { Accept: 'application/json' } }).then((r) => r.json());
  const codes = (cart.discount_codes ?? []).map((d) => String(d.code));
  if (!codes.some((c) => c.toUpperCase() === code)) return;
  const keep = codes.filter((c) => c.toUpperCase() !== code).join(',');
  await fetch(`${root}cart/update.js`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: JSON.stringify({ discount: keep }),
  });
}

if (!window.__impulsCardCodeBound) {
  window.__impulsCardCodeBound = true;
  document.addEventListener(
    'click',
    async (event) => {
      const btn = event.target instanceof Element ? event.target.closest('.es-popup-button') : null;
      if (!(btn instanceof HTMLElement) || !cardCode() || btn.dataset.impulsCodeChecked === 'true') return;
      // Hold the app's click until the cart is cleaned, then replay it.
      event.preventDefault();
      event.stopImmediatePropagation();
      btn.dataset.impulsCodeChecked = 'true';
      try {
        await removeCardCode();
      } catch {
        // The form still opens; worst case the cart keeps its codes.
      }
      btn.click();
      window.setTimeout(() => delete btn.dataset.impulsCodeChecked, 1500);
    },
    true
  );
}

// Cart checkout button: go through the card discount link (applies the code, then opens checkout).
if (!window.__impulsCardLinkBound) {
  window.__impulsCardLinkBound = true;
  document.addEventListener(
    'click',
    (event) => {
      const btn = event.target instanceof Element ? event.target.closest('[data-impuls-card-href]') : null;
      if (!(btn instanceof HTMLElement) || btn.hasAttribute('disabled')) return;
      event.preventDefault();
      event.stopImmediatePropagation();
      window.location.assign(btn.getAttribute('data-impuls-card-href') ?? '/checkout');
    },
    true
  );
}
